import { AUTH_LOGIN, AUTH_LOGOUT, AUTH_CHECK, AUTH_ERROR, useGetPermissions } from 'react-admin';
import storage from './storage';
import { useEffect, useState } from 'react';

export const authProvider = (loginApiUrl, noAccessPage = '/login') => {
   return {
    login: params => {
      
      // add realm info. 
      if (params.username === 'admin') {
        params.realm = 'f';
      }
      else if (params.username === 'service@fooding.io') {
        params.realm = 'c';
      }
      else {
        params.realm = 'a';
      }

      const request = new Request(loginApiUrl, {
        method: 'POST',
        body: JSON.stringify(params),
        headers: new Headers({ 'Content-Type': 'application/json' }),
      });
      return fetch(request)
        .then(response => {
          if (response.status < 200 || response.status >= 300) {
            throw new Error(response.statusText);
          }
          return response.json();
        })
        .then(({ ttl, ...data }) => {
          const { user } = data;
          if (user.username !== 'admin' && user.customerId !== 93) {
            throw new Error('푸딩 직원만 접근 가능!');
          }
          // role 
          data.role = getRole(data.user);
          storage.save('lbtoken', data, ttl);
        });
    },
    logout: () => {
      storage.remove('lbtoken');
      return Promise.resolve();
    },
    checkAuth: () => {
      const token = storage.load('lbtoken');
      if (token && token.id) {
        return Promise.resolve();
      } else {
        storage.remove('lbtoken');
        return Promise.reject({ redirectTo: noAccessPage });
      }
    },
    checkError: error => {
      const { status } = error;
      if (status === 401 || status === 403) {
        storage.remove('lbtoken');
        return Promise.reject();
      }
      return Promise.resolve();
    },
    getIdentity: () => {
      const token = storage.load('lbtoken');
      if (token && token.id) {
        return Promise.resolve({
          id: token.userId,
          fullName: token.user?.name ?? null
        });
      } else {
        storage.remove('lbtoken');
        return Promise.reject({ redirectTo: noAccessPage });
      }
    },
    getPermissions: params => {
      if (params && params.resource && params.action) {
        const role = storage.load('lbtoken')?.role;
        const resource = params.resource;
        const action = params.action;
        const result = hasPermission(role, resource, action);
        return result ? Promise.resolve(result) : Promise.reject('No access');
      }
      else {
        // resource, action 없는 경우는 legacy 처리 
        return Promise.resolve();
      }
    }
  }
};

/// helpers 

export const useHasPermissions = (resource, action, onFail) => {

  const [ hasPermission, setHasPermission ] = useState(false);

  const getPermissions = useGetPermissions();

  useEffect(() => {
    getPermissions({
      resource, 
      action,
    }).then((result) => {
      setHasPermission(result);
    }).catch((error) => {
      setHasPermission(false);
      if (onFail) {
        onFail(error);
      }
    })
  }, []);

  return hasPermission;
}

export const useAccessibleResources = () => {

  const [ resList, setResList ] = useState([]);

  const role = storage.load('lbtoken')?.role;

  useEffect(() => {
    if (role) {

      if (role === Roles.Admin) {
        setResList(Object.values(Resources));
        return;
      }

      const permissions = acGrid[role];
      const resources = permissions
        .filter(p => p.action.includes(Actions.List))
        .map(p => p.res);
      setResList(resources);
    }
    else {
      setResList([]);
    }
    console.log('useGetResources.useEffect', role)
  }, [ role ]);

  return resList;
}

export const Roles = {
  Admin: 'admin',
  FA: 'fa',
  OP: 'op',
  MD: 'md',
  FF: 'ff',
  CS: 'cs',
  Dev: 'dev'
};

export const Resources = {
  Customer: 'customer',
  Supplier: 'supplier',
  Menu: 'menu',
  CustomerOrders: 'customer-orders',
  CustomerBill: 'customer-bill',
  SupplierOrders: 'supplier-orders',
  SupplierBill: 'supplier-bill',
};

export const Actions = {
  List: 'list',
  Show: 'show',
  Create: 'create',
  Edit: 'edit',
  Delete: 'delete',
};

const getRole = (user) => {
  if (user.username === 'admin') {
    return Roles.Admin;
  }
  if (!user.teamId) {
    return null;
  }
  // Note: lb3 api 추가 개발 한계로 teamId 하드코딩된 값 사용 
  switch (user.teamId) {
    case 7:   // FA
      return Roles.FA;
    case 8:   // MD
      return Roles.MD;
    case 9:   // OP
      return Roles.OP;
    case 10: // FF
      return Roles.FF;
    case 11: // Dev
      return Roles.Dev;
    case 12: // CS
      return Roles.CS;
    case 13: // admin
      return Roles.Admin;
    default:
      return null;
  }
}

const hasPermission = (role, resource, action) => {
  const permissions = acGrid[role];
  if (!permissions) {
    return false;
  }
  if (permissions.find(p => p.res === '*' && p.action.includes('*'))) {
    return true;
  }
  const permission = permissions.find(p => p.res === resource);
  if (!permission) {
    return false;
  }
  if (permission.action.includes(action)) {
    return true;
  }
  return false;
}

const acGrid = {
  admin: [
    { res: '*', action: '*' }
  ],
  fa: [
    { res: 'customer', action: [ 'list', 'show', 'create', 'edit', 'delete' ] },
    { res: 'supplier', action: [ 'list', 'show', 'create', 'edit', 'delete' ] },
    { res: 'menu', action: [ 'list', 'show' ] },
    { res: 'customer-orders', action: [ 'list', 'show' ] },
    { res: 'customer-bill', action: [ 'list', 'show' ] },
    { res: 'supplier-orders', action: [ 'list', 'show' ] },
    { res: 'supplier-bill', action: [ 'list', 'show' ] },
  ],
  op: [
    { res: 'customer', action: [ 'list', 'show', 'create', 'edit', 'delete' ] },
    { res: 'supplier', action: [ 'list', 'show' ] },
    { res: 'menu', action: [ 'list', 'show' ] },
    { res: 'customer-orders', action: [ 'list', 'show' ] },
    { res: 'supplier-orders', action: [ 'list', 'show' ] },
  ],
  md: [
    { res: 'customer', action: [ 'list', 'show', 'edit' ] },
    { res: 'supplier', action: [ 'list', 'show', 'create', 'edit', 'delete' ] },
    { res: 'menu', action: [ 'list', 'show', 'create', 'edit', 'delete' ] },
    { res: 'customer-orders', action: [ ] },
    { res: 'supplier-orders', action: [ 'list', 'show' ] },
  ],
  ff: [
    { res: 'customer', action: [ 'list', 'show', 'edit' ] },
    { res: 'supplier', action: [ 'list', 'show', 'edit' ] },
    { res: 'menu', action: [ 'list', 'show' ] },
    { res: 'customer-orders', action: [ ] },
    { res: 'supplier-orders', action: [ ] },
  ],
  cs: [
    { res: 'customer', action: [ 'list', 'show', 'edit' ] },
    { res: 'supplier', action: [ 'list', 'show', 'edit' ] },
    { res: 'menu', action: [ 'list', 'show' ] },
    { res: 'customer-orders', action: [ 'list', 'show' ] },
    { res: 'supplier-orders', action: [ 'list', 'show' ] },
  ],
  dev: [
    { res: 'customer', action: [ 'list', 'show', 'create', 'edit', 'delete' ] },
    { res: 'supplier', action: [ 'list', 'show', 'create', 'edit', 'delete' ] },
    { res: 'menu', action: [ 'list', 'show', 'create', 'edit', 'delete' ] },
    { res: 'customer-orders', action: [ 'list', 'show' ] },
    { res: 'customer-bill', action: [ 'list', 'show' ] },
    { res: 'supplier-orders', action: [ 'list', 'show' ] },
    { res: 'supplier-bill', action: [ 'list', 'show' ] },
  ],
}
